import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    StudentPersonalInfoViewModel,
    Organization,
    StudentPersonalInfoResponse,
    Metadata,
    isFormFilled,
    hasFailedInstantMatch,
    FormFooterComponent as FormFooter,
    updateViewModelOrganization, updateFieldValidationErrorsByFieldId, handleEmailOnKeyDown, submitForm,
    setRef,
    VerificationStepsEnum,
    MarketConsentWrapper,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
} from '@sheerid/jslib';

interface StepStudentPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

function StepStudentPersonalInfo({ intl, verificationService }: StepStudentPersonalInfoComponentProps) {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    return (

        <div id="sid-step-student-personal-info" className="sid-l-container">
            <img className="sid-header__logo" src="https://assets-resources.sheerid.com/jcrew/img/jcrew-logo-cursive.png" alt="" />
            { failedInstantMatch
                ? (
                    <div className="sid-header">

                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                        </div>

                        <div className="sid-header__subtitle-info sid-l-horz-center sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and birthdate must match what is on your official school records."
                            />
                        </div>
                    </div>
                )
                : (
                    <div className="sid-header">

                        <div className="sid-header__subtitle-info sid-l-horz-center">
                            <FormattedHTMLMessage
                                id="step.personalInfo.subtitle"
                                defaultMessage="Complete the college student form below to confirm your eligibility."
                            />
                        </div>

                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                        </div>
                    </div>
                )}

            <div className="sid-sub-header-all-fields-text">
                <FormattedHTMLMessage
                    id="step.personalInfo.reqFields"
                    defaultMessage="All fields are required."
                />
            </div>

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.universityPlaceholder',
                        defaultMessage: 'University',
                    })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: 'step.personalInfo.firstNamePlaceholder',
                            defaultMessage: 'First name',
                        })
                    }
                    onChange={
                        (newValue:string) => {
                            updateStudentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: 'step.personalInfo.lastNamePlaceholder',
                            defaultMessage: 'Last name',
                        })
                    }
                    onChange={
                        (newValue:string) => {
                            updateStudentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: 'step.personalInfo.emailPlaceholder',
                        defaultMessage: 'Email',
                    })
                }
                onChange={
                    (newValue:string) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: any) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />


            <div className="sid-privacy-policy-container">
                <span>
                    <FormattedHTMLMessage id="step.personalInfo.privacy" defaultMessage="To view our Privacy Policy <a href='https://www.jcrew.com/help/privacy-policy' target='_blank' class='sid-privacy-link'>click here</a>" />

                </span>
            </div>

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        ref={(button:HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch
                            ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            : <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />}
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="studentInfoShared"
                    defaultMessage="Information entered here will be used for verification purposes."
                />
                {
                    viewModel.countryChoice && viewModel.countryChoice.value !== 'US'
                        ? (
                            <div>
                                <FormattedHTMLMessage
                                    id="informationTransferredToUS"
                                    defaultMessage="Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                                />

                            </div>
                        )

                        : null
                }

                <div className="sid-last-footer">
                    <span className="sid-footer-one">
                        <FormattedHTMLMessage
                            id="step.personalInfo.lastFooter"
                            defaultMessage="Verification services hosted by SheerID."
                        />
                    </span>

                    <p className="sid-footer-two">
                        <FormattedHTMLMessage
                            id="step.personalInfo.FooterOne"
                            defaultMessage="<a href='https://verify.sheerid.com/student-faq/' target='_blank' class='sid-footer-two'>SheerID FAQs</a> &nbsp;"
                        />
                        <FormattedHTMLMessage
                            id="step.personalInfo.FooterTwo"
                            defaultMessage="<a href='https://www.sheerid.com/privacy-policy/' target='_blank' class='sid-footer-two'>SheerID Privacy Policy</a>"
                        />
                    </p>
                </div>
            </FormFooter>
        </div>
    );
}

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
